import React, { ComponentType, useCallback, useContext, useEffect, useMemo } from 'react';

import { Box, Icon } from '@rbilabs/component-library';
import { defaultTo, isEqualWith } from 'lodash-es';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { isFalse } from 'utils';

import { IBaseProps } from '@rbi-ctg/frontend';
import { IStore } from '@rbi-ctg/store';
import { useRefreshRestaurantAvailabilityMutation } from 'generated/rbi-graphql';
import useDialogModal, { Props as DialogProps } from 'hooks/use-dialog-modal';
import useEffectOnce from 'hooks/use-effect-once';
import useMediaQuery from 'hooks/use-media-query';
import { usePrevious } from 'hooks/use-previous';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useRestaurantPosConnectivityStatus } from 'state/restaurant-connectivity-status';
import { useServiceModeContext } from 'state/service-mode';
import LocalStorage from 'utils/cognito/storage';
import { StorageKeys } from 'utils/local-storage';
import {
  isMobileOrderingAvailable,
  isRestaurantOpen,
  mergeRestaurantData,
  useGetRestaurantFn
} from 'utils/restaurant';
import { routes } from 'utils/routing';
import { isCatering } from 'utils/service-mode';

import {
  IStoreContext,
  StorePermissions,
  curriedGetStoreStatusV1,
  curriedGetStoreStatusV2,
  useAnyStore,
  useStore
} from './hooks';
import { useStoreUtils } from './hooks/use-store-utils';

export * from './hooks/types';

export const StoreContext = React.createContext<IStoreContext>({} as IStoreContext);
export const useStoreContext = () => useContext(StoreContext);

const styleDialog = (Dialog: ComponentType) => styled(Dialog)`
  background-color: ${Styles.color.white} !important;
  ${Styles.mobile`
    width: 100vw;
    height: 100vh;
    display: flex;
    flex-flow: column nowrap;
    align-items: center;
    justify-content: center;
  `}
`;

export const StoreProvider = ({ children }: IBaseProps) => {
  const enableOrdering = defaultTo(useFlag(LaunchDarklyFlag.ENABLE_ORDERING), true);
  const enableStoreSelection2_0 = useFlag(LaunchDarklyFlag.ENABLE_STORE_SELECTION_2_0);
  // If flags are enabled, refresh restaurant availability & poll for the selected store's availability to notify the user when the store becomes unavailable.
  const refreshRestaurantAvailability = useFlag(LaunchDarklyFlag.REFRESH_RESTAURANT_AVAILABILITY);

  const getRestaurant = useGetRestaurantFn();
  const [triggerRefreshRestaurantAvailability] = useRefreshRestaurantAvailabilityMutation();
  const { formatMessage } = useIntl();
  const { location, navigate } = useLocationContext();
  const { serviceMode } = useServiceModeContext();

  const useStoreResult = useStore();
  const useAnyStoreResult = useAnyStore();

  const {
    resetStore,
    setStore,
    store,
    storeProxy,
    selectUnavailableStore,
    onConfirmStoreChange
  } = enableStoreSelection2_0 ? useAnyStoreResult : useStoreResult;
  const curriedGetStoreStatus = enableStoreSelection2_0
    ? curriedGetStoreStatusV2
    : curriedGetStoreStatusV1;

  const {
    fetchStore,
    disableChickenSandwich,
    email,
    isRestaurantAvailable,
    prices,
    serviceModeStatus,
    resetLastTimeStoreUpdated
  } = useStoreUtils({
    resetStore,
    store: storeProxy
  });

  const isDesktop = useMediaQuery('desktop');
  const getCurrentStoreStatus = useMemo(
    () =>
      curriedGetStoreStatus({
        store,
        serviceModeStatuses: serviceModeStatus,
        selectedServiceMode: serviceMode
      }),
    [curriedGetStoreStatus, serviceMode, serviceModeStatus, store]
  );

  const {
    isStoreOpenAndAvailable,
    isStoreOpenAndUnavailable,
    isStoreClosed,
    noStoreSelected
  } = useMemo(() => {
    const status = getCurrentStoreStatus();

    return {
      isStoreOpenAndAvailable: status === StorePermissions.OPEN_AND_AVAILABLE,
      isStoreOpenAndUnavailable: status === StorePermissions.OPEN_AND_UNAVAILABLE,
      isStoreClosed: status === StorePermissions.CLOSED,
      noStoreSelected: status === StorePermissions.NO_STORE_SELECTED
    };
  }, [getCurrentStoreStatus]);

  // If flags are enabled, refresh restaurant availability & poll for the selected store's availability to notify the user when the store becomes unavailable.
  const storeAvailabilityPolling = useFlag(LaunchDarklyFlag.STORE_AVAILABILITY_POLLING);

  const { isRestaurantPosOnline } = useRestaurantPosConnectivityStatus(
    storeProxy?.number,
    storeAvailabilityPolling
  );

  const onConfirmLocateRestaurants = useCallback(() => {
    resetStore();

    if (location.pathname !== routes.storeLocator) {
      navigate(routes.storeLocator);
    }
  }, [location.pathname, navigate, resetStore]);

  const fetchAndSetStore = useCallback(
    async (storeId: string, hasSelection = true) => {
      const fetchedStore = await fetchStore(storeId);

      if (fetchedStore) {
        setStore({ ...fetchedStore, hasSelection });
      }
    },
    [fetchStore, setStore]
  );

  const [OrderingUnavailableDialog, openOrderingUnavailableDialog] = useDialogModal({
    onConfirm: onConfirmLocateRestaurants,
    onDismiss: resetStore,
    modalAppearanceEventMessage: 'Ordering is unavailable'
  });

  const [StoreChangeDialog, openStoreChangeDialog] = useDialogModal({
    onConfirm: onConfirmStoreChange,
    showCancel: true,
    modalAppearanceEventMessage: 'Confirmation: Store change'
  });

  // typescript can't infer types here, maybe because selectStoreDialog is renamed when it is returned
  const selectStoreDialog = useCallback(
    async (newStore: IStore, hasCartItems: boolean, callback: () => void) => {
      const open =
        isCatering(serviceMode) ||
        isRestaurantOpen(newStore.diningRoomHours) ||
        isRestaurantOpen(newStore.driveThruHours);

      const { number: storeId } = newStore;

      // we do not care about POS if ordering is not enabled
      // extra cautious here - newStore is typed as any
      const rbiRestaurant = await getRestaurant(storeId);
      const isRestaurantPosAvailable = rbiRestaurant.available;

      // operating hours now come from our gql layer
      // so we must merge the results with the sanity store
      newStore = mergeRestaurantData({ rbiRestaurant, sanityStore: newStore });

      // ensure store did not close between render and selection
      if (newStore && !open) {
        return openOrderingUnavailableDialog();
      }

      if (
        enableOrdering &&
        // restaurant has no posData
        (!newStore.restaurantPosData ||
          // has posData, but heartbeat is false
          !isRestaurantPosAvailable ||
          // store has no mobile ordering available
          !isMobileOrderingAvailable(newStore))
      ) {
        return openOrderingUnavailableDialog({
          message: formatMessage({
            id: 'storeNoOrderingBody'
          }),
          title: formatMessage({
            id: 'storeNoOrderingHeading'
          })
        });
      }
      if (refreshRestaurantAvailability) {
        triggerRefreshRestaurantAvailability({
          variables: {
            storeId
          }
        });
      }
      if (isStoreOpenAndUnavailable) {
        return onConfirmStoreChange({ newStore, callback });
      }
      if (isEqualWith(storeProxy.physicalAddress, newStore.physicalAddress)) {
        return callback();
      }
      if (!hasCartItems) {
        return onConfirmStoreChange({
          newStore,
          callback
        });
      }
      openStoreChangeDialog({
        newStore,
        callback
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      enableOrdering,
      formatMessage,
      isStoreOpenAndUnavailable,
      onConfirmStoreChange,
      openOrderingUnavailableDialog,
      openStoreChangeDialog,
      refreshRestaurantAvailability,
      serviceMode,
      storeProxy.physicalAddress,
      triggerRefreshRestaurantAvailability
    ]
  );

  useEffectOnce(() => {
    // Fetch store on initial render if a store is selected
    if (store && 'number' in store && store.number !== null) {
      const storeHasSelection = !!store && 'hasSelection' in store && store.hasSelection;
      fetchAndSetStore(store.number, storeHasSelection);
    }
  });

  useEffect(() => {
    // We need to explicitly check for false since a restaurant's availability always begins as null when refreshing/logging out.
    if (!noStoreSelected && isStoreOpenAndAvailable && isFalse(isRestaurantPosOnline)) {
      return openOrderingUnavailableDialog();
    }
  }, [
    isRestaurantPosOnline,
    isStoreOpenAndAvailable,
    noStoreSelected,
    openOrderingUnavailableDialog
  ]);

  useEffect(() => {
    LocalStorage.setItem(StorageKeys.DISABLE_CHICKEN_SANDWICH, disableChickenSandwich);
  }, [disableChickenSandwich]);

  const previousEnableStoreSelection2_0 = usePrevious(enableStoreSelection2_0);
  useEffect(() => {
    if (
      // Reset store when the LD flag changes to make sure we don't pass the wrong store type
      !!previousEnableStoreSelection2_0 !== !!enableStoreSelection2_0
    ) {
      resetStore();
    }
  }, [enableStoreSelection2_0, previousEnableStoreSelection2_0, resetStore]);

  const StyledOrderingUnavailableDialog = styleDialog(OrderingUnavailableDialog) as ComponentType<
    DialogProps<{}>
  >;

  const value = useMemo(
    () => ({
      disableChickenSandwich,
      email,
      isRestaurantAvailable,
      openOrderingUnavailableDialog,
      prices,
      selectStore: selectStoreDialog,
      fetchStore: fetchAndSetStore,
      selectUnavailableStore,
      serviceModeStatus,
      setStore,
      resetStore,
      resetLastTimeStoreUpdated,
      getCurrentStoreStatus,
      isStoreOpenAndAvailable,
      isStoreOpenAndUnavailable,
      isStoreClosed,
      noStoreSelected,
      store: storeProxy
    }),
    [
      disableChickenSandwich,
      email,
      fetchAndSetStore,
      getCurrentStoreStatus,
      isRestaurantAvailable,
      isStoreClosed,
      isStoreOpenAndAvailable,
      isStoreOpenAndUnavailable,
      noStoreSelected,
      openOrderingUnavailableDialog,
      prices,
      resetLastTimeStoreUpdated,
      resetStore,
      selectStoreDialog,
      selectUnavailableStore,
      serviceModeStatus,
      setStore,
      storeProxy
    ]
  );

  return (
    <StoreContext.Provider value={value}>
      {children}
      <StoreChangeDialog
        body={formatMessage({ id: 'changeStoreMessage' })}
        heading={formatMessage({ id: 'changeStores' })}
      />
      <StyledOrderingUnavailableDialog
        body={formatMessage({
          id: enableOrdering ? 'cannotPlaceOrder' : 'closeMessageToFindNearbyRestaurants'
        })}
        buttonLabel={formatMessage({ id: 'findRestaurants' })}
        heading={formatMessage({ id: 'storeClosed' })}
        image={
          <Box show={isDesktop} margin="0 17rem">
            <Icon icon="error" color="primary" width="6.125rem" height="6.125rem" />
          </Box>
        }
      />
    </StoreContext.Provider>
  );
};

export default StoreContext.Consumer;
